"use client";

/**
 * @file ListHeader.tsx
 * @description 경매 리스트 페이지 전용 헤더 컴포넌트
 *
 * 용도: 경매 리스트 페이지 (/list) 전용
 * 특징: BIDIX 로고 + 검색창 + 시뮬레이션 버튼 + 사용자 아바타
 * 디자인: 간단하고 깔끔한 레이아웃
 */

import Link from "next/link";
import dynamic from "next/dynamic";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { Search } from "lucide-react";

// UserButton을 클라이언트 사이드에서만 렌더링하여 hydration 오류 방지
const UserButton = dynamic(
  () => import("@clerk/nextjs").then((mod) => mod.UserButton),
  { ssr: false }
);
import { Button } from "@/components/shared/ui/button";
import { MobileBackButton } from "@/components/shared/common/MobileBackButton";
import { searchAuctionAction } from "@/app/action/auction/search-auction";

export function ListHeader() {
  const router = useRouter();

  // 검색 상태 관리
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  /**
   * 검색 실행 함수
   * HeroSection과 동일한 로직 적용
   */
  const handleSearch = async () => {
    const query = searchQuery.trim();
    if (!query) return;

    console.log("[ListHeader] 검색 실행:", query);
    setIsSearching(true);

    try {
      // Server Action으로 사건번호 검색
      const result = await searchAuctionAction({ caseNo: query });

      // 타입 가드: error가 있으면 에러 처리
      if ("error" in result) {
        // 사건번호 검색 실패 시 키워드 검색으로 폴백
        console.log("[ListHeader] 사건번호 검색 실패, 키워드 검색으로 폴백:", result.error);
        
        // 키워드 검색: 현재 페이지에서 필터 적용
        const regions = ['서울특별시', '경기도', '인천광역시', '부산광역시', '대구광역시', '광주광역시', '대전광역시', '울산광역시', '세종특별자치시', '강원특별자치도', '충청북도', '충청남도', '전라북도', '전라남도', '경상북도', '경상남도', '제주특별자치도'];
        const matchedRegion = regions.find(region => {
          const regionShort = region.replace(/특별시|광역시|특별자치시|특별자치도|도/g, '').trim();
          return region.includes(query) || query.includes(region) || regionShort.includes(query) || query.includes(regionShort);
        });

        const propertyTypes = ['아파트', '오피스텔', '빌라', '다세대', '단독주택', '상가', '토지', '공장', '창고'];
        const matchedPropertyType = propertyTypes.find(type => type.includes(query) || query.includes(type));

        if (!matchedRegion && !matchedPropertyType) {
          alert(`'${query}'에 대한 검색 결과를 찾을 수 없습니다.`);
          return;
        }

        // 필터 적용된 URL로 이동
        const searchParams = new URLSearchParams();
        if (matchedRegion) {
          searchParams.set('regions', matchedRegion);
        }
        if (matchedPropertyType) {
          searchParams.set('types', matchedPropertyType);
        }
        router.push(`/list?${searchParams.toString()}`);
        return;
      }

      // 타입 가드: data가 있고 url이 있으면 단일 결과 → 분석 페이지로 이동
      if ("data" in result && result.data && "url" in result.data) {
        router.push(result.data.url);
        return;
      }

      // 타입 가드: data가 있고 items가 있으면 다중 결과 → 첫 번째 항목으로 이동
      if ("data" in result && result.data && "items" in result.data && result.data.items.length > 0) {
        const firstItem = result.data.items[0];
        const analysisUrl = `/analysis/${encodeURIComponent(firstItem.caseNo)}/${firstItem.disposalSeq}`;
        router.push(analysisUrl);
        return;
      }

      // 결과 없음
      alert("해당 물건을 찾을 수 없습니다.");
    } catch (error) {
      console.error("[ListHeader] 검색 중 오류:", error);
      alert("사건번호 검색 중 오류가 발생했습니다.");
    } finally {
      setIsSearching(false);
    }
  };

  /**
   * Enter 키 입력 처리
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <header className="bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-700 sticky top-0 z-50">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3 sm:gap-8">
          {/* 모바일 뒤로가기 버튼 */}
          <MobileBackButton className="shrink-0" />
          
          <Link href="/" className="text-2xl font-black tracking-tighter hover:text-zinc-600 dark:hover:text-zinc-300 transition shrink-0">
            BIDIX
          </Link>

          {/* Desktop Search */}
          <div className="hidden md:flex items-center bg-zinc-100 dark:bg-zinc-800 rounded-full px-4 py-2.5 w-96 border border-transparent focus-within:border-zinc-300 dark:focus-within:border-zinc-600 focus-within:bg-white dark:focus-within:bg-zinc-800 focus-within:shadow-sm transition-all">
            <Search className={`w-4 h-4 text-zinc-400 dark:text-zinc-500 ${isSearching ? 'animate-pulse' : ''}`} />
            <input
              type="text"
              placeholder="지역, 매물유형, 사건번호 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSearching}
              className="bg-transparent border-none outline-none text-sm ml-2 w-full placeholder:text-zinc-400 dark:placeholder:text-zinc-500 font-medium text-zinc-900 dark:text-zinc-100 disabled:opacity-50"
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* 대시보드로 이동 버튼 */}
          <Link href="/dashboard">
            <Button
              variant="ghost"
              className="text-zinc-900 dark:text-zinc-100 hover:text-black dark:hover:text-white font-medium"
            >
              내 검토 기록
            </Button>
          </Link>

          <UserButton
            afterSignOutUrl="/"
            appearance={{
              elements: { avatarBox: "h-9 w-9" },
            }}
          />
        </div>
      </div>
    </header>
  );
}
